import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { ButtonToggle } from '@datachain/ui-sdk/components/app-toggle-buttons';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import { ContentReadyEvent } from 'devextreme/ui/data_grid';
import { Map } from 'immutable';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';

import { DxOperations } from '../../../../core/dx-types';
import { DcIcons } from '../../../../ui/app-dc.icons';
import { DcLoaders } from '../../../../ui/app-loaders';
import { ExpositionsSelector, refreshAvailableConsumers } from '../../../store';
import {
	AccessConsumerItemEntity,
	SelectedConsumer,
} from './access-consumer-item.entity';
import { AccessConsumerListEntity } from './access-consumer-list.entity';
import {
	AccessConsumerMappingPopupProvider,
	ConsumerType,
} from './access-consumer-mapping-popup.provider';

@Component({
	selector: 'app-access-consumer-mapping-popup',
	templateUrl: './access-consumer-mapping-popup.component.html',
	styleUrls: ['./access-consumer-mapping-popup.component.scss'],
	providers: [AccessConsumerMappingPopupProvider],
})
export class AccessConsumerMappingPopupComponent<
		T extends { selectedConsumers: Map<number, SelectedConsumer> }
	>
	extends DcBaseComponent
	implements OnInit
{
	public consumerTypes: Array<ButtonToggle> = [
		{
			label: $localize`:i18n=@@exposition.access.consumer.type.users:`,
			id: 'get-users',
			isSelected: true,
			icon: DcIcons.User,
			cb: (): void => this.consumerType$.next(ConsumerType.User),
		},
		{
			label: $localize`:i18n=@@exposition.access.consumer.type.groups:`,
			id: 'get-groups',
			isSelected: false,
			icon: DcIcons.Group,
			cb: (): void => this.consumerType$.next(ConsumerType.Group),
		},
	];
	public consumerType$ = new BehaviorSubject<ConsumerType>(ConsumerType.User);
	public StringFilterOperations = [
		// added here and not in core/dx-types to avoid merge conflicts
		DxOperations.Contains,
		DxOperations.NotContains,
		DxOperations.Equal,
		DxOperations.NotEqual,
	];
	public vo$: Observable<{
		consumerList: AccessConsumerListEntity;
		selectedConsumers: Array<SelectedConsumer>;
		initialSelectedConsumers: Map<number, SelectedConsumer>;
		consumerType: ConsumerType;
	}>;

	private readonly selectedConsumers$ = new BehaviorSubject<
		Array<SelectedConsumer>
	>(this.modalData.selectedConsumers.toList().toArray());

	private readonly initialSelectedConsumers$ = new BehaviorSubject<
		Map<number, SelectedConsumer>
	>(this.modalData.selectedConsumers);

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	protected readonly DcIcons = DcIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly ConsumerType = ConsumerType;
	public constructor(
		private readonly store: Store,
		private readonly selector: ExpositionsSelector,
		private readonly modalService: AppModalService,
		private readonly consumerMappingProvider: AccessConsumerMappingPopupProvider,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'access-consumer-mapping-popup';
		const consumerList$ = combineAll({
			elements: this.selector.getAvailableConsumers$(),
			consumerType: this.consumerType$,
		}).pipe(
			map(({ elements, consumerType }) => {
				const filteredElements =
					consumerType === ConsumerType.Group
						? elements.filter((item) => item.isGroup)
						: elements.filter((item) => !item.isGroup);
				const itemsMap = filteredElements.reduce((acc, item) => {
					const entity = AccessConsumerItemEntity.buildConsumer({
						id: item.id,
						firstName: item.firstName,
						lastName: item.lastName,
						mail: item.mail,
						description: item.description,
						avatar: item.avatar,
						name: item.name,
						isGroup: item.isGroup,
					});
					return acc.set(`${entity.id}`, entity);
				}, Map<string, AccessConsumerItemEntity>());
				const list = this.consumerMappingProvider.setListElements(
					consumerType,
					itemsMap
				);
				this.grid?.instance.refresh(true);
				return list;
			})
		);

		this.vo$ = combineAll({
			consumerList: consumerList$,
			selectedConsumers: this.selectedConsumers$,
			initialSelectedConsumers: this.initialSelectedConsumers$,
			consumerType: this.consumerType$,
		});
	}

	public async removeSelectedConsumer(id: number): Promise<void> {
		const isSelected = this.grid?.instance.isRowSelected(id);
		if (isSelected) {
			this.grid?.instance.deselectRows([id]);
			return;
		}
		const initialSelectedConsumers = await firstValueFrom(
			this.initialSelectedConsumers$
		);

		const updateInitialConsumers = initialSelectedConsumers.delete(id);
		this.initialSelectedConsumers$.next(updateInitialConsumers);

		const selected = this.getSelectedRows();

		this.selectedConsumers$.next([
			...selected,
			...updateInitialConsumers.toList().toArray(),
		]);
	}

	public async onSelectionChanged(): Promise<void> {
		const selected = this.getSelectedRows();

		const initialSelectedConsumers = await firstValueFrom(
			this.initialSelectedConsumers$
		);
		const allSelected: Array<SelectedConsumer> =
			initialSelectedConsumers.size === 0
				? selected
				: [...selected, ...initialSelectedConsumers.toList().toArray()];
		this.selectedConsumers$.next(allSelected);
	}
	public async onGridContentReady(event: ContentReadyEvent): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			this.firstLoadSubject.next(false);
		}

		const initialSelectedConsumers = await firstValueFrom(
			this.initialSelectedConsumers$
		);

		if (initialSelectedConsumers.size === 0) {
			return;
		}

		const currentVisibleRowIds = event.component
			.getVisibleRows()
			.map<number>((r) => r.key);

		const selectedConsumersInCurrentPage = initialSelectedConsumers.filter(
			(key) => currentVisibleRowIds.includes(key.id)
		);

		if (selectedConsumersInCurrentPage.size === 0) {
			return;
		}
		const updated = initialSelectedConsumers.filter(
			(key) => !currentVisibleRowIds.includes(key.id)
		);
		this.initialSelectedConsumers$.next(updated);
		this.grid?.instance.selectRows(
			initialSelectedConsumers.keySeq().toArray(),
			true
		);
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public refresh(): void {
		this.store.dispatch(refreshAvailableConsumers());
	}

	public async saveAction(): Promise<void> {
		const selectedConsumers = await firstValueFrom(this.selectedConsumers$);

		this.modalService.closeActiveModal({
			selectedConsumers: Map(selectedConsumers.map((item) => [item.id, item])),
		});
	}

	private getSelectedRows(): Array<SelectedConsumer> {
		const selectedRows =
			this.grid?.instance.getSelectedRowsData() as Array<AccessConsumerItemEntity>;

		return selectedRows.map<SelectedConsumer>((row) => ({
			id: row.id,
			name: row.name,
			isGroup: row.isGroup,
			avatar: row.avatar,
		}));
	}
}
